import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  InlineStack,
  Text,
  Pagination,
  BlockStack,
  SkeletonBodyText,
  EmptyState,
  Box,
  Modal,
  TextField,
} from '@shopify/polaris';
import { returnsApi, assetsApi } from '@/services';
import type { Asset } from '@/types';

export default function ReturnsRepair() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [repairNotes, setRepairNotes] = useState('');
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['repair-queue', page],
    queryFn: () => returnsApi.getRepairQueue({ page, limit }),
  });

  const repairCompleteMutation = useMutation({
    mutationFn: (data: { serialNumber: string; notes?: string }) =>
      assetsApi.repairComplete(data.serialNumber, data.notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair-queue'] });
      queryClient.invalidateQueries({ queryKey: ['qc-queue'] });
      queryClient.invalidateQueries({ queryKey: ['returns-stats'] });
      setShowRepairModal(false);
      setSelectedAsset(null);
      setRepairNotes('');
    },
  });

  const retireMutation = useMutation({
    mutationFn: (data: { serialNumber: string; reason: string }) =>
      assetsApi.retire(data.serialNumber, data.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair-queue'] });
      queryClient.invalidateQueries({ queryKey: ['returns-stats'] });
      setShowRepairModal(false);
      setSelectedAsset(null);
      setRepairNotes('');
    },
  });

  const handleOpenRepair = useCallback((asset: Asset) => {
    setSelectedAsset(asset);
    setRepairNotes('');
    setShowRepairModal(true);
  }, []);

  const handleRepairComplete = useCallback(() => {
    if (!selectedAsset) return;
    repairCompleteMutation.mutate({
      serialNumber: selectedAsset.serialNumber,
      notes: repairNotes || undefined,
    });
  }, [selectedAsset, repairNotes, repairCompleteMutation]);

  const handleRetire = useCallback(() => {
    if (!selectedAsset || !repairNotes.trim()) return;
    retireMutation.mutate({
      serialNumber: selectedAsset.serialNumber,
      reason: repairNotes,
    });
  }, [selectedAsset, repairNotes, retireMutation]);

  const rows = (data?.data || []).map((asset) => [
    asset.serialNumber,
    asset.productSku,
    asset.productName,
    asset.notes || 'No notes',
    new Date(asset.updatedAt).toLocaleDateString(),
    <Badge key={asset.id} tone="warning">In Repair</Badge>,
    <Button key={`action-${asset.id}`} onClick={() => handleOpenRepair(asset)}>
      Update
    </Button>,
  ]);

  if (error) {
    return (
      <Page title="Repair Queue">
        <Card>
          <EmptyState
            heading="Error loading repair queue"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Repair Queue">
      <Card padding="0">
        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={10} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No items in repair"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>All items are in working condition.</p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={['Serial', 'SKU', 'Product', 'Issue', 'Date', 'Status', 'Action']}
              rows={rows}
            />
            {data && data.meta.totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < data.meta.totalPages}
                    onPrevious={() => setPage((p) => p - 1)}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>

      {data && (
        <Box paddingBlockStart="400">
          <Text as="p" tone="subdued" alignment="center">
            {data.meta.total} items in repair
          </Text>
        </Box>
      )}

      {/* Repair Modal */}
      <Modal
        open={showRepairModal}
        onClose={() => {
          setShowRepairModal(false);
          setSelectedAsset(null);
          setRepairNotes('');
        }}
        title={`Repair - ${selectedAsset?.serialNumber}`}
      >
        <Modal.Section>
          {selectedAsset && (
            <BlockStack gap="400">
              <Card>
                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Product</Text>
                    <Text as="span">{selectedAsset.productName}</Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">SKU</Text>
                    <Text as="span">{selectedAsset.productSku}</Text>
                  </InlineStack>
                  {selectedAsset.notes && (
                    <>
                      <Text as="span" tone="subdued">Previous Notes</Text>
                      <Text as="p">{selectedAsset.notes}</Text>
                    </>
                  )}
                </BlockStack>
              </Card>

              <TextField
                label="Repair Notes"
                value={repairNotes}
                onChange={setRepairNotes}
                multiline={3}
                placeholder="Describe the repair performed or reason for retirement..."
                autoComplete="off"
              />

              <InlineStack gap="200">
                <div style={{ flex: 1 }}>
                  <Button
                    fullWidth
                    tone="success"
                    onClick={handleRepairComplete}
                    loading={repairCompleteMutation.isPending}
                  >
                    Repair Complete
                  </Button>
                </div>
                <div style={{ flex: 1 }}>
                  <Button
                    fullWidth
                    tone="critical"
                    onClick={handleRetire}
                    loading={retireMutation.isPending}
                    disabled={!repairNotes.trim()}
                  >
                    Retire Asset
                  </Button>
                </div>
              </InlineStack>

              <Text as="p" tone="subdued">
                Completed repairs will go back to QC for final inspection.
              </Text>
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
